import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Select, type Step, Steps, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useToast } from '../../../utils/ToastContext'
import { queryClient, trpc } from '../../../utils/trpc'

const steps: Step[] = [{ label: 'Cycle Roles' }, { label: 'Map Roles' }, { label: 'Unmapped Roles' }]
const stepKeys = ['cycleRoles', 'mapRoles', 'unmappedRoles']

export function CycleRolesTab() {
	const { organizationId, cycleId } = useParams<{ organizationId: string; cycleId: string }>()
	const navigate = useNavigate()
	const location = useLocation()
	const [selectedCustomField, setSelectedCustomField] = useState('')
	const [currentStep, setCurrentStep] = useState(0)
	const [completed, setCompleted] = useState<boolean[]>([false, false, false])
	const toast = useToast()

	const { data: roleStepData } = useQuery({
		...trpc.admin.cycles.getRoleStep.queryOptions({
			organizationId: organizationId as string,
			cycleId: cycleId as string,
		}),
		enabled: !!organizationId && !!cycleId,
	})

	useEffect(() => {
		if (!roleStepData) return
		const completedSteps = [false, false, false]
		let step = 0
		const currentRoleStep = roleStepData[0]?.roleStep ?? ''

		switch (currentRoleStep) {
			case 'cycleRoles':
				completedSteps[0] = true
				step = 1
				break
			case 'mapRoles':
				completedSteps[0] = completedSteps[1] = true
				step = 2
				break
			case 'unmappedRoles':
				completedSteps[0] = completedSteps[1] = completedSteps[2] = true
				step = 2
				break
		}

		setCompleted((prev) => (prev.every((v, i) => v === completedSteps[i]) ? prev : completedSteps))
		setCurrentStep((prev) => (prev === step ? prev : step))
	}, [roleStepData])

	useEffect(() => {
		const targetPath = `/organizations/${organizationId}/cycles/${cycleId}/roles/${stepKeys[currentStep]}`

		if (location.pathname !== targetPath) {
			navigate(targetPath)
		}
	}, [currentStep, organizationId, cycleId, location.pathname, navigate])

	const roleStepMutation = useMutation({
		...trpc.admin.cycles.updateRoleStep.mutationOptions({}),
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.getRoleStep.queryKey({
					organizationId: organizationId as string,
					cycleId: cycleId as string,
				}),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const resetCycleRolesMutation = useMutation({
		...trpc.admin.cycles.roles.mappings.reset.mutationOptions({
			onSuccess: () => {
				toast.showToast('Cycle roles restarted')
			},
			onError: () => {
				toast.showToast('Error restarting cycle roles', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.roles.list.queryKey({ cycleId: cycleId as string }),
					exact: false,
					refetchType: 'all',
				})
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.roles.permissions.list.queryKey({
						cycleId: cycleId as string,
						organizationId,
					}),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const handleStepChange = (idx: number) => {
		if (idx !== currentStep) {
			setCurrentStep(idx)
		}
	}

	const markStepCompleted = async (idx: number) => {
		await updateRoleStep(stepKeys[idx])
		setCompleted((prev) => {
			const copy = [...prev]
			copy[idx] = true
			return copy
		})
		queryClient.invalidateQueries({
			queryKey: [['cyclesRoles']],
			refetchType: 'all',
		})
		const nextStep = idx + 1
		if (nextStep < stepKeys.length) {
			setCurrentStep(nextStep)
			navigate(`/organizations/${organizationId}/cycles/${cycleId}/roles/${stepKeys[nextStep]}`)
		}
	}

	const updateRoleStep = async (step: string) => {
		await roleStepMutation.mutateAsync({
			organizationId: organizationId as string,
			cycleId: cycleId as string,
			roleStep: step,
		})
	}

	const resetSteps = async () => {
		setCompleted([false, false, false])
		setCurrentStep(0)
		setSelectedCustomField('')
		await resetCycleRolesMutation.mutateAsync({ cycleId: cycleId as string })
		await updateRoleStep('')
		queryClient.invalidateQueries({
			queryKey: [['cyclesRoles']],
			refetchType: 'all',
		})
		queryClient.invalidateQueries({
			queryKey: [['cycles', 'getRoleStep']],
			refetchType: 'all',
		})
		navigate(`/organizations/${organizationId}/cycles/${cycleId}/roles/cycleRoles`)
	}

	const { data: customFields, isLoading: isCustomFieldsLoading } = useQuery({
		...trpc.admin.users.getCustomFields.queryOptions({
			organizationId: organizationId as string,
		}),
		enabled: !!organizationId && currentStep === 1,
	})

	const { data: existingRoleField } = useQuery({
		...trpc.admin.cycles.roles.list.queryOptions({
			cycleId: cycleId as string,
			perPage: 1,
		}),
		enabled: !!cycleId && currentStep === 1,
		select: (data) => data.data[0]?.roleField ?? '',
	})

	useEffect(() => {
		if (existingRoleField) {
			setSelectedCustomField(existingRoleField)
		}
	}, [existingRoleField])

	const handleCustomFieldValueChange = (customField: string) => {
		setSelectedCustomField(customField)
	}

	return (
		<div className='px-6 mt-6'>
			<div className=' flex flex-col bg-white shadow-table rounded-lg'>
				<div className='flex justify-between flex-row gap-4 border-b-neutral-200 border-b-1 mb-5'>
					<Steps
						steps={steps.map((s, i) => ({ ...s, completed: completed[i] }))}
						currentStep={currentStep}
						onStepChange={handleStepChange}
						clickable={[true, true, true]}
					/>
					{currentStep === 1 && !isCustomFieldsLoading && (
						<div className='flex items-center gap-3 mr-6'>
							<Text variant='small' className='whitespace-nowrap'>
								Users file column
							</Text>
							<Select
								value={selectedCustomField}
								options={
									customFields?.map((customField) => ({
										value: customField,
										label: customField,
									})) || []
								}
								onChange={handleCustomFieldValueChange}
								placeholder='Select a custom field'
								disabled={completed[1]}
							/>
						</div>
					)}
					{currentStep === 2 && (
						<div className='flex items-center mr-6'>
							<Button onClick={() => resetSteps()} variant='primary'>
								Restart
							</Button>
						</div>
					)}
				</div>
				<Outlet
					context={{
						selectedCustomField,
						currentStep,
						completed,
						markStepCompleted,
						goToStep: handleStepChange,
					}}
				/>
			</div>
		</div>
	)
}
